// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Alert, Button, Stack, Text } from '@mantine/core';
import type { Practitioner } from '@medplum/fhirtypes';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useState } from 'react';
import { FormCard } from '../../../components/FormCard';
import { getIntakeBotClient } from '../../../lib/bot-client';
import { botIdentifier } from '../../../lib/bot-identifier';
import { getTelecom } from '../../../utils/practitioner';

function getPractitionerEmail(practitioner: Practitioner): string {
  return getTelecom(practitioner, 'email').trim();
}

interface PasswordSectionProps {
  practitioner: Practitioner;
}

export function PasswordSection({ practitioner }: PasswordSectionProps): JSX.Element {
  const email = getPractitionerEmail(practitioner);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleRequestReset(): Promise<void> {
    if (!email) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const client = await getIntakeBotClient();
      await client.executeBot(botIdentifier('request-provider-password-reset'), { email }, 'application/json');
      setSubmitted(true);
    } catch (err) {
      Sentry.captureException(err);
      setError('We could not send a reset link right now. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <FormCard
      title="Password"
      action={
        <Button size="xs" onClick={handleRequestReset} loading={loading} disabled={!email}>
          {submitted ? 'Send again' : 'Email reset link'}
        </Button>
      }
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          We&apos;ll email a secure link to reset the password for your provider portal account.
        </Text>
        {!email && (
          <Alert color="yellow" title="No email on file">
            Add an email address to your profile before requesting a password reset.
          </Alert>
        )}
        {submitted && email && (
          <Alert color="blue" title="Check your email">
            We&apos;ve sent a password reset link to the email address on file.
          </Alert>
        )}
        {error && (
          <Alert color="red" title="Unable to send reset link">
            {error}
          </Alert>
        )}
      </Stack>
    </FormCard>
  );
}
